import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorStatusSelector } from '@store/selectors/ErrorStatusSelector';
import { Box, Grid, IconButton, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import { clearErrorStatus } from '@store/actions/ErrorStatusActionCreators';
import { ChatAwareModal } from '@components/ChatAwareComponents/ChatAwareModal/ChatAwareModal';

const useStyles = makeStyles((theme: Theme) => ({
	modal: {
		display: 'flex',
		justifyContent: 'center',
		marginTop: theme.spacing(10)
	},
	paper: {
		backgroundColor: theme.palette.background.paper,
		border: 0,
		boxShadow: theme.shadows[5],
		padding: theme.spacing(4),
		outline: 'none',
		maxWidth: '50vw',
		height: 'fit-content',
		width: theme.spacing(80)
	},
	titleText: {
		fontSize: 32,
		fontWeight: 500,
		textAlign: 'left'
	},
	cancelIcon: {
		width: 20,
		color: theme.palette.grey['500'],
	},
	errorContentSection: {
		fontSize: 18,
		justifyContent: 'center',
		fontWeight: 600,
		textAlign: 'left',
		marginTop: theme.spacing(2),
		lineHeight: `${theme.spacing(3)}px`
	},
	disclaimerContainer: {
		fontSize: 15,
		textAlign: 'left',
		marginTop: theme.spacing(2),
		letterSpacing: '0.25px',
		lineHeight: `${theme.spacing(3)}px`
	},
	disclaimerTextBold: {
		display: 'block',
		fontWeight: 600
	},
	disclaimerText: {
		display: 'block'
	}
}));

const ErrorStatusModal = () => {
	const dispatch = useDispatch();
	const classes = useStyles();
	const { status, code, errorCode, timestamp } = useSelector(ErrorStatusSelector);
	const cancelClickHandler = () => dispatch(clearErrorStatus());
	const formattedTimestamp = timestamp && new Date(timestamp).toISOString().replace('T', ' ').slice(0, -5)+'Z';
	const displayError = React.useMemo(() => {
		if (typeof code === 'string') {
			if (code.includes(status?.toString())) {
				return code;
			}
			return `${status}${code}`;
		}
		if (errorCode) {
			return errorCode;
		}
		if (status) {
			return `${status}UNK`;
		}
	}, [code, errorCode, status]);

	return (
		<Box>
			<ChatAwareModal open={!!displayError} className={classes.modal}>
				<Box className={classes.paper}>
					<Grid container direction="row" justifyContent="space-between">
						<Grid className={classes.titleText}>Error</Grid>
						<Grid>
							<IconButton
								onClick={cancelClickHandler}
								data-field="app-genericErrorModal-closeIcon"
								aria-label="Close error modal"
							>
								<CancelIcon className={classes.cancelIcon}/>
							</IconButton>
						</Grid>
					</Grid>
					<Box>
						<Box className={classes.errorContentSection}>
							<Box id="error">
								Error { displayError }
							</Box>
							<Box>
								Timestamp: { formattedTimestamp }
							</Box>
						</Box>
						<Box className={classes.disclaimerContainer}>
							<Typography className={classes.disclaimerText}>Please call your service help desk number and reference this error code and timestamp.</Typography>
						</Box>
					</Box>
				</Box>
			</ChatAwareModal>
		</Box>
	);
};

export default ErrorStatusModal;
